import React, {useState} from 'react';
import {ShadInput} from "../../../../shadCn/components/ui/ShadInput";
import AlertModal from "../../../../components/basic/AlertModal";

const DEFAULT_LABELS = {
    title: "값 설정",
    description: "선택할 수 있는 값과 계층을 설정합니다.",
    rootPlaceholder: "값 이름",
    addRootLabel: "+ 값 추가",
    addChildLabel: "+ 하위값",
    childPlaceholder: "하위 값 이름",
    deleteTitle: "선택한 값을 삭제하시겠습니까?",
    deleteDescription: "하위 값이 있으면 삭제할 수 없습니다.",
    emptyMessage: "등록된 값이 없습니다. 위에서 값을 추가하세요.",
};

function OptionTreeNode({
    node,
    depth,
    onCreateChild,
    onUpdate,
    onDelete,
    labels,
}) {
    const [addingChild, setAddingChild] = useState(false);
    const [childName, setChildName] = useState("");
    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState(node.name ?? "");

    const submitChild = async () => {
        const trimmed = childName.trim();
        if (!trimmed) {
            return;
        }
        await onCreateChild(node.id, trimmed);
        setChildName("");
        setAddingChild(false);
    };

    const submitEdit = async () => {
        const trimmed = editName.trim();
        if (!trimmed) {
            return;
        }
        await onUpdate({...node, name: trimmed});
        setEditing(false);
    };

    const cancelEdit = () => {
        setEditing(false);
        setEditName(node.name ?? "");
    };

    return (
        <li className="code-option-tree__node">
            <div className="code-option-tree__row" style={editing ? undefined : {paddingLeft: `${depth * 16 + 8}px`}}>
                {editing ? (
                    <>
                        <div className="code-option-tree__field">
                            <ShadInput
                                className="code-option-tree__input basic-input input-style"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        submitEdit();
                                    }
                                    if (e.key === "Escape") {
                                        cancelEdit();
                                    }
                                }}
                                autoFocus
                            />
                        </div>
                        <div className="code-option-tree__actions">
                            <button type="button" className="code-manage-confirm-btn code-manage-btn--compact" onClick={submitEdit}>
                                확인
                            </button>
                            <button type="button" className="code-manage-neutral-btn code-manage-btn--compact" onClick={cancelEdit}>
                                취소
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <span className="code-option-tree__name">{node.name}</span>
                        <div className="code-option-tree__actions">
                            <button type="button" className="code-manage-neutral-btn code-manage-btn--compact" onClick={() => setAddingChild(true)}>
                                {labels.addChildLabel}
                            </button>
                            <button
                                type="button"
                                className="save-btn code-manage-btn--compact"
                                onClick={() => {
                                    setEditing(true);
                                    setEditName(node.name ?? "");
                                }}
                            >
                                수정
                            </button>
                            <AlertModal
                                triggerName="삭제"
                                handler={() => onDelete(node.id)}
                                triggerClassName="code-manage-delete-btn code-manage-btn--compact"
                                actionName="삭제하기"
                                title={labels.deleteTitle}
                                description={labels.deleteDescription}
                            />
                        </div>
                    </>
                )}
            </div>

            {addingChild && (
                <div className="code-option-tree__add-row">
                    <div className="code-option-tree__field">
                        <ShadInput
                            className="code-option-tree__input basic-input input-style"
                            value={childName}
                            placeholder={labels.childPlaceholder}
                            onChange={(e) => setChildName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    submitChild();
                                }
                                if (e.key === "Escape") {
                                    setAddingChild(false);
                                    setChildName("");
                                }
                            }}
                            autoFocus
                        />
                    </div>
                    <div className="code-option-tree__actions">
                        <button type="button" className="save-btn code-manage-btn--compact" onClick={submitChild}>추가</button>
                        <button
                            type="button"
                            className="code-manage-neutral-btn code-manage-btn--compact"
                            onClick={() => {
                                setAddingChild(false);
                                setChildName("");
                            }}
                        >
                            취소
                        </button>
                    </div>
                </div>
            )}

            {node.children?.length > 0 && (
                <ul className="code-option-tree__children">
                    {node.children.map((child) => (
                        <OptionTreeNode
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            onCreateChild={onCreateChild}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                            labels={labels}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

function CodeOptionTreeEditor({parentId, options = [], onCreate, onUpdate, onDelete, labels: labelsProp, hideHeader = false}) {
    const [rootName, setRootName] = useState("");
    const labels = {...DEFAULT_LABELS, ...labelsProp};

    const handleCreateRoot = async () => {
        const trimmed = rootName.trim();
        if (!trimmed) {
            return;
        }
        await onCreate(parentId, trimmed);
        setRootName("");
    };

    const handleCreateChild = async (parentNodeId, name) => {
        await onCreate(parentNodeId, name);
    };

    return (
        <div className="code-option-tree">
            {!hideHeader && (
                <div className="code-option-tree__header">
                    <h5 className="code-option-tree__title">{labels.title}</h5>
                    <p className="code-option-tree__desc">{labels.description}</p>
                </div>
            )}
            <div className="code-editor__content">
                <div className="code-option-tree__add-root">
                    <div className="code-option-tree__field">
                        <ShadInput
                            className="code-option-tree__input basic-input input-style"
                            value={rootName}
                            placeholder={labels.rootPlaceholder}
                            onChange={(e) => setRootName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleCreateRoot();
                                }
                            }}
                        />
                    </div>
                    <button type="button" className="save-btn code-manage-btn--compact" onClick={handleCreateRoot}>
                        {labels.addRootLabel}
                    </button>
                </div>

                {options.length > 0 ? (
                    <ul className="code-option-tree__list">
                        {options.map((node) => (
                            <OptionTreeNode
                                key={node.id}
                                node={node}
                                depth={0}
                                onCreateChild={handleCreateChild}
                                onUpdate={onUpdate}
                                onDelete={onDelete}
                                labels={labels}
                            />
                        ))}
                    </ul>
                ) : (
                    <p className="code-option-tree__empty">{labels.emptyMessage}</p>
                )}
            </div>
        </div>
    );
}

export default CodeOptionTreeEditor;
